// src/pages/ModeratorFormBuilder.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  IconButton,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Radio,
  Checkbox,
  FormControlLabel,
  Select,
  InputLabel,
  FormControl
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Forum as ForumIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import CodeIcon from '@mui/icons-material/Code'
import { db } from '../firebase'
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  getDocs
} from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'

// i18n
import { useTranslation } from 'react-i18next'

// Quill (metin editörü)
import ReactQuill from 'react-quill'
import Quill from 'quill'
import 'react-quill/dist/quill.snow.css'
import 'quill/dist/quill.core.css'
import { uploadFile } from '../utils/uploadFile'

// react-beautiful-dnd
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

// -------------------------------------------------------------
// Quill setup
function useQuillModules() {
  const quillRef = useRef(null)
  const { t } = useTranslation() // Eğer link / image / video prompt'larını da çeviriye bağlamak isterseniz.

  const handleImage = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        const editor = quillRef.current.getEditor()
        const range = editor.getSelection(true)
        editor.insertEmbed(range.index, 'image', url, Quill.sources.USER)
        editor.setSelection(range.index + 1, 0)

        setTimeout(() => {
          const editorContainer = editor.container
          const newlyInsertedImage = editorContainer.querySelector(`img[src="${url}"]`)
          if (newlyInsertedImage && !newlyInsertedImage.style.width) {
            newlyInsertedImage.style.maxWidth = '100%'
            newlyInsertedImage.style.height = 'auto'
          }
        }, 0)
      } catch (err) {
        console.error('Görsel yüklenirken hata:', err)
      }
    }
  }, [])

  const handleVideo = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'video/*')
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        const editor = quillRef.current.getEditor()
        const range = editor.getSelection(true)
        editor.insertEmbed(range.index, 'video', url, Quill.sources.USER)
      } catch (err) {
        console.error('Video yüklenirken hata:', err)
      }
    }
  }, [])

  const handleLink = useCallback(() => {
    const editor = quillRef.current.getEditor()
    const range = editor.getSelection()
    if (range && range.length > 0) {
      // Burada prompt’u da çeviriye bağlayabilirsiniz (isteğe bağlı).
      const href = prompt('Lütfen linki giriniz:')
      if (href) {
        editor.format('link', href)
      }
    } else {
      alert('Önce link vereceğiniz metni seçiniz.')
    }
  }, [])

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image', 'video'],
          ['clean']
        ],
        handlers: {
          link: handleLink,
          image: handleImage,
          video: handleVideo
        }
      }
    }),
    [handleImage, handleVideo, handleLink]
  )

  return { quillRef, modules }
}

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'link',
  'image',
  'video',
  'align',
  'direction'
]


function InlineQuillEditor({ value, onChange }) {
  const { quillRef, modules } = useQuillModules()
  const [anchorEl, setAnchorEl] = useState(null)
  const [clickedImage, setClickedImage] = useState(null)

  useEffect(() => {
    if (!quillRef.current) return
    const editor = quillRef.current.getEditor()
    const root = editor.root

    const handleClick = (e) => {
      if (e.target && e.target.tagName === 'IMG') {
        setClickedImage(e.target)
        setAnchorEl(e.target)
      } else {
        handleCloseMenu()
      }
    }

    root.addEventListener('click', handleClick)
    return () => {
      root.removeEventListener('click', handleClick)
    }
  }, [quillRef])

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setClickedImage(null)
  }

  const handleSelectSize = (size) => {
    if (clickedImage) {
      clickedImage.style.width = size + '%'
    }
    handleCloseMenu()
  }

  return (
    <>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        style={{ minHeight: '200px' }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => handleSelectSize(25)}>%25</MenuItem>
        <MenuItem onClick={() => handleSelectSize(50)}>%50</MenuItem>
        <MenuItem onClick={() => handleSelectSize(100)}>%100</MenuItem>
      </Menu>
    </>
  )
}



// -------------------------------------------------------------
// ModeratorFormBuilder Bileşeni
export default function ModeratorFormBuilder() {
  const { currentUser } = useAuth()
  const { t } = useTranslation() // <== i18n Hook

  const [workspaceMap, setWorkspaceMap] = useState({})

  // Kanallar
  const [channels, setChannels] = useState([])
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [loadingChannels, setLoadingChannels] = useState(true)

  // Soru Formları
  const [questionForms, setQuestionForms] = useState([])
  const [loadingQuestionForms, setLoadingQuestionForms] = useState(false)
  const [selectedQuestionForm, setSelectedQuestionForm] = useState(null)

  // Sorular
  const [questions, setQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)

  // Yeni form ismi
  const [newFormName, setNewFormName] = useState('')

  // Yeni soru
  const [newQuestionText, setNewQuestionText] = useState('')
  const [newQuestionType, setNewQuestionType] = useState('openEnded')
  const [newChoices, setNewChoices] = useState([''])
  const [newAllowOther, setNewAllowOther] = useState(false)
  const [newRequireMedia, setNewRequireMedia] = useState(false)
  const [newRequired, setNewRequired] = useState(false)

  // Rating
  const [newRatingMin, setNewRatingMin] = useState(1)
  const [newRatingMax, setNewRatingMax] = useState(5)
  const [newRatingName, setNewRatingName] = useState('')

  // AI-Moderator
  const [newAiPrompt, setNewAiPrompt] = useState('')
  const [newAiCount, setNewAiCount] = useState(1)

  const [newCards, setNewCards] = useState([]); // [{ text: '', imageUrl: '' }, ...]
  const [newGroups, setNewGroups] = useState([]); // ['Grup A', 'Grup B', ...]

  // Yeni dialog state
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    form: null,
    newName: ''
  })

  // Rename dialog açma
  const handleOpenRenameDialog = (form) => {
    setRenameDialog({
      open: true,
      form,
      newName: form.name || ''
    })
  }

  // Kaydetme
  const handleSaveRename = async () => {
    if (!renameDialog.newName.trim()) {
      setSnackbar({
        open: true,
        message: 'Form adı boş bırakılamaz!',
        severity: 'warning'
      })
      return
    }
    try {
      await updateDoc(doc(db, 'questionForms', renameDialog.form.id), {
        name: renameDialog.newName.trim()
      })
      setRenameDialog({ open: false, form: null, newName: '' })
      setSnackbar({
        open: true,
        message: 'Form ismi güncellendi!',
        severity: 'success'
      })
    } catch (error) {
      console.error('Form adı güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: 'Form adını güncellemede hata oluştu.',
        severity: 'error'
      })
    }
  }

  // İptal
  const handleCancelRename = () => {
    setRenameDialog({ open: false, form: null, newName: '' })
  }

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }))
  }

  // Edit Dialog
  const [editDialog, setEditDialog] = useState({
    open: false,
    question: null,
    updatedText: '',
    updatedType: 'openEnded',
    updatedChoices: [],
    updatedAllowOther: false,
    updatedRequireMedia: false,
    updatedRequired: false,
    updatedRatingMin: 1,
    updatedRatingMax: 5,
    updatedRatingName: '',
    updatedAiPrompt: '',
    updatedAiCount: 1,
    updatedCards: [],
    updatedGroups: []
  })

  // YENİ EKLENDİ: Tüm workspace'leri alıp bir map oluşturuyoruz.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'workspaces'), (snapshot) => {
      const wMap = {}
      snapshot.forEach((docSnap) => {
        wMap[docSnap.id] = docSnap.data().name
      })
      setWorkspaceMap(wMap)
    })
    return () => unsubscribe()
  }, [])

  // ------------------ Kanallar Çekme ------------------
  useEffect(() => {
    if (!currentUser) return

    const qChannel = query(
      collection(db, 'channels'),
      where('members', 'array-contains', currentUser.uid)
    )
    const unsubscribe = onSnapshot(
      qChannel,
      (snapshot) => {
        const allChannels = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        // Sadece moderatör olunan kanalları listele
        const filteredChs = allChannels.filter(
          (channel) =>
            channel.memberDetails &&
            channel.memberDetails.some(
              (m) => m.userId === currentUser.uid && m.role === 'moderator'
            )
        )
        setChannels(filteredChs)
        setLoadingChannels(false)
      },
      (error) => {
        console.error('Kanallar alınırken hata oluştu:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.channelsLoadingError'),
          severity: 'error'
        })
        setLoadingChannels(false)
      }
    )

    return () => unsubscribe()
  }, [currentUser, t])

  // ------------------ Soru Formu Listesi ------------------
  useEffect(() => {
    if (!selectedChannel) return

    setLoadingQuestionForms(true)
    const qForms = query(
      collection(db, 'questionForms'),
      where('channelId', '==', selectedChannel.id),
      orderBy('createdAt', 'asc')
    )

    const unsubscribe = onSnapshot(
      qForms,
      (snapshot) => {
        const forms = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        setQuestionForms(forms)
        setLoadingQuestionForms(false)
      },
      (error) => {
        console.error('Soru formları alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.questionFormsLoadingError'),
          severity: 'error'
        })
        setLoadingQuestionForms(false)
      }
    )

    return () => unsubscribe()
  }, [selectedChannel, t])

  // ------------------ Soru Listesi ------------------
  useEffect(() => {
    if (!selectedChannel || !selectedQuestionForm) return

    setLoadingQuestions(true)
    const qQuestions = query(
      collection(db, 'questions'),
      where('channelId', '==', selectedChannel.id),
      where('questionFormId', '==', selectedQuestionForm.id),
      orderBy('sortOrder', 'asc')
    )

    const unsubscribe = onSnapshot(
      qQuestions,
      (snapshot) => {
        const qs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        setQuestions(qs)
        setLoadingQuestions(false)
      },
      (error) => {
        console.error('Sorular alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.questionsLoadingError'),
          severity: 'error'
        })
        setLoadingQuestions(false)
      }
    )

    return () => unsubscribe()
  }, [selectedChannel, selectedQuestionForm, t])

  // =======================================================
  //                     Soru Formu İşlemleri
  // =======================================================
  const handleCreateQuestionForm = async () => {
    const trimmedName = newFormName.trim()
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameEmpty'),
        severity: 'warning'
      })
      return
    }
    if (!selectedChannel) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.selectChannelFirst'),
        severity: 'warning'
      })
      return
    }

    try {
      await addDoc(collection(db, 'questionForms'), {
        channelId: selectedChannel.id,
        name: trimmedName,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'draft'
      })
      setNewFormName('')
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formAddedDraft'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Form eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formAddError'),
        severity: 'error'
      })
    }
  }

  const handleDeleteQuestionForm = async (formId) => {
    if (!window.confirm(t('moderatorFormBuilder.deleteFormConfirm'))) {
      return
    }
    try {
      await deleteDoc(doc(db, 'questionForms', formId))
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formDeleted'),
        severity: 'success'
      })
      if (selectedQuestionForm && selectedQuestionForm.id === formId) {
        setSelectedQuestionForm(null)
      }
    } catch (error) {
      console.error('Form silinirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formDeleteError'),
        severity: 'error'
      })
    }
  }

  // Form Durum Güncellemeleri
  async function handlePublish(form) {
    try {
      await updateDoc(doc(db, 'questionForms', form.id), {
        status: 'published'
      })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishSuccess', { formName: form.name }),
        severity: 'success'
      })
    } catch (error) {
      console.error(error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishError'),
        severity: 'error'
      })
    }
  }

  async function handleMarkComplete(form) {
    if (!window.confirm(t('moderatorFormBuilder.completeConfirm'))) return
    try {
      await updateDoc(doc(db, 'questionForms', form.id), { status: 'completed' })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.completeSuccess', { formName: form.name }),
        severity: 'success'
      })
    } catch (error) {
      console.error(error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.completeError'),
        severity: 'error'
      })
    }
  }

  async function handleRevertToDraft(form) {
    if (!window.confirm(t('moderatorFormBuilder.revertConfirm'))) return
    try {
      // 1) Tüm responses sil
      const respQuery = query(
        collection(db, 'responses'),
        where('questionFormId', '==', form.id)
      )
      const respSnap = await getDocs(respQuery)
      const deletes = []
      respSnap.forEach((docSnap) => {
        deletes.push(deleteDoc(doc(db, 'responses', docSnap.id)))
      })
      await Promise.all(deletes)

      // 2) draft’a geri al
      await updateDoc(doc(db, 'questionForms', form.id), { status: 'draft' })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.revertSuccess', { formName: form.name }),
        severity: 'success'
      })
    } catch (error) {
      console.error(error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.revertError'),
        severity: 'error'
      })
    }
  }

 

  async function handleSendReminderEmails(form) {
      try {
        // 1) İlgili channel'ı bul
        const channel = channels.find((c) => c.id === form.channelId)
        if (!channel) {
          setSnackbar({
            open: true,
            message: 'Kanal bulunamadı.',
            severity: 'error'
          })
          return
        }

        // 2) Bu formu tamamlamış userId'leri bul (responses.status = 'completed')
        const respQuery = query(
          collection(db, 'responses'),
          where('channelId', '==', channel.id),
          where('questionFormId', '==', form.id)
        )
        const respSnap = await getDocs(respQuery)
        
        const completedUserIds = respSnap.docs
          .filter((docSnap) => docSnap.data().status === 'completed')
          .map((docSnap) => docSnap.data().userId)

        // 3) Kanal üyelerinden bu listede OLMAYAN kişiler = incomplete
        const incompleteMembers = (channel.memberDetails || []).filter(
          (m) => m.role === 'participant' && !completedUserIds.includes(m.userId)
        )
        if (incompleteMembers.length === 0) {
          setSnackbar({
            open: true,
            message: 'Tamamlanmayan kimse yok, e-posta gönderilmedi.',
            severity: 'info'
          })
          return
        }

        // 4) "incomplete" userId’lerinin displayName ve email bilgilerini "users" koleksiyonundan çek
        const incompleteUserIds = incompleteMembers.map((m) => m.userId)

        // -- Not: Eğer incompleteUserIds sayısı 10’dan fazlaysa "where in" sorgusu kısıtı var.
        //    10’dan fazla id için isterseniz chunk (böl) mantığı uygulayabilirsiniz.
        //    Basit bir durumda 10'dan az olduğunu varsayalım.
        const usersRef = collection(db, 'users')
        const qUsers = query(usersRef, where('uid', 'in', incompleteUserIds))
        const userSnap = await getDocs(qUsers)

        // 5) Kullanıcı verilerini alıp, Cloud Function’a göndereceğimiz dizi oluştur
        const userArray = userSnap.docs.map((docSnap) => {
          const data = docSnap.data()
          return {
            name: data.nickname || '',
            email: data.email || ''
          }
        })

        console.log(userArray)
        // Eğer userArray tamamen boşsa (ya da email eksikse) yine uyarı verebilirsiniz.
        if (userArray.length === 0) {
          setSnackbar({
            open: true,
            message: 'Kullanıcı bilgisi bulunamadı veya eksik, e-posta gönderilmedi.',
            severity: 'info'
          })
          return
        }

        // 6) Cloud Function’a POST isteği at (FormData ile)
        const formData = new FormData()
        // "users" isimli bir alan oluşturup, içine userArray bilgisini string olarak ekleyelim.
        formData.append('users', JSON.stringify(userArray))

        // 6) Cloud Function’a POST isteği at
        const response = await fetch(
          'https://europe-west3-evosurvey.cloudfunctions.net/qip-evoo-comminity-email',
          {
            method: 'POST',
            body: formData
          }
        )

        if (!response.ok) {
          throw new Error('Cloud Function yanıtı başarısız oldu.')
        }

        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.emailReminderSuccess'),
          severity: 'success'
        })
      } catch (error) {
        console.error('Hatırlatma gönderim hatası:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.emailReminderError'),
          severity: 'error'
        })
      }
  }

// Kart İşleyicileri
const handleAddCard = () => {
  setNewCards([...newCards, { text: '', imageUrl: '' }]);
};

const handleRemoveCard = (index) => {
  const updated = newCards.filter((_, i) => i !== index);
  setNewCards(updated);
};

const handleCardTextChange = (index, value) => {
  const updated = [...newCards];
  updated[index].text = value;
  setNewCards(updated);
};

const handleUploadCardImage = async (index) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;
    try {
      const url = await uploadFile(file);
      const updated = [...newCards];
      updated[index].imageUrl = url;
      setNewCards(updated);
    } catch (err) {
      console.error('Fotoğraf yükleme hatası:', err);
      setSnackbar({
        open: true,
        message: 'Fotoğraf yüklenirken hata oluştu.',
        severity: 'error'
      });
    }
  };
  input.click();
};

// Grup İşleyicileri
const handleAddGroup = () => {
  setNewGroups([...newGroups, '']);
};

const handleRemoveGroup = (index) => {
  const updated = newGroups.filter((_, i) => i !== index);
  setNewGroups(updated);
};

const handleGroupChange = (index, value) => {
  const updated = [...newGroups];
  updated[index] = value;
  setNewGroups(updated);
};

  // =======================================================
  //                      Soru İşlemleri
  // =======================================================
  const handleAddQuestion = async () => {
    if (!selectedQuestionForm) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.selectChannelFirst'),
        severity: 'warning'
      })
      return
    }
    if (selectedQuestionForm.status !== 'draft') {
      // Bu form yayınlanmış veya tamamlanmış.
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionError'), // isteğe bağlı ek key
        severity: 'warning'
      })
      return
    }

    // Soru metni boş mu?
    const plainText = newQuestionText.replace(/<[^>]+>/g, '').trim()
    if (!plainText) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionTextEmptyWarning'),
        severity: 'warning'
      })
      return
    }
    if (!selectedChannel) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.selectChannelFirst'),
        severity: 'warning'
      })
      return
    }

    let trimmedChoices = newChoices.map((c) => c.trim()).filter((c) => c !== '')

    if (
      (newQuestionType === 'singleChoice' || newQuestionType === 'multipleChoice') &&
      trimmedChoices.length === 0
    ) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.choicesRequiredWarning'),
        severity: 'warning'
      })
      return
    }

    // Rating question
    if (newQuestionType === 'ratingQuestion') {
      if (!newRatingName.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingNameWarning'),
          severity: 'warning'
        })
        return
      }
      if (newRatingMin >= newRatingMax) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingMinMaxWarning'),
          severity: 'warning'
        })
        return
      }
    }

    // AI-Moderator question
    if (newQuestionType === 'aiModerator') {
      if (!newAiPrompt.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiPromptEmptyWarning'),
          severity: 'warning'
        })
        return
      }
      if (newAiCount < 1) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiCountMinWarning'),
          severity: 'warning'
        })
        return
      }
    }

    if (newQuestionType === 'cardSorting') {
      if (newCards.length === 0 || newGroups.length === 0) {
        setSnackbar({
          open: true,
          message: 'Kart sıralama soruları en az bir kart ve bir grup gerektirir.',
          severity: 'warning'
        });
        return;
      }
    }

    try {
      const sortOrderValue = questions.length
      await addDoc(collection(db, 'questions'), {
        channelId: selectedChannel.id,
        questionFormId: selectedQuestionForm.id,
        text: newQuestionText,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        questionType: newQuestionType,
        cards: newQuestionType === 'cardSorting' ? newCards : [],
        groups: newQuestionType === 'cardSorting' ? newGroups : [],
        choices: trimmedChoices,
        allowOther: newAllowOther,
        sortOrder: sortOrderValue,
        requireMedia: newQuestionType === 'openEnded' ? newRequireMedia : false,
        isRequired: newRequired,
        ratingMin: newRatingMin,
        ratingMax: newRatingMax,
        ratingName: newRatingName,
        aiPrompt: newAiPrompt,
        aiCount: newAiCount
      })

      // Temizle
      setNewQuestionText('')
      setNewQuestionType('openEnded')
      setNewChoices([''])
      setNewAllowOther(false)
      setNewRequireMedia(false)
      setNewRequired(false)
      setNewRatingMin(1)
      setNewRatingMax(5)
      setNewRatingName('')
      setNewAiPrompt('')
      setNewAiCount(1)
      setNewCards([]);
      setNewGroups([]);

      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionError'),
        severity: 'error'
      })
    }
  }

  const handleEditQuestion = (question) => {
    setEditDialog({
      open: true,
      question,
      updatedText: question.text,
      updatedType: question.questionType || 'openEnded',
      updatedChoices: question.choices || [],
      updatedAllowOther: question.allowOther || false,
      updatedRequireMedia: question.requireMedia || false,
      updatedRequired: question.isRequired || false,
      updatedRatingMin: question.ratingMin || 1,
      updatedRatingMax: question.ratingMax || 5,
      updatedRatingName: question.ratingName || '',
      updatedAiPrompt: question.aiPrompt || '',
      updatedAiCount: question.aiCount || 1,
      updatedCards: question.cards || [],
      updatedGroups: question.groups || []
    })
  }

  const handleSaveEdit = async () => {
    if (selectedQuestionForm?.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'), // isteğe bağlı
        severity: 'warning'
      })
      setEditDialog((prev) => ({ ...prev, open: false }))
      return
    }

    const {
      question,
      updatedText,
      updatedType,
      updatedChoices,
      updatedAllowOther,
      updatedRequireMedia,
      updatedRequired,
      updatedRatingMin,
      updatedRatingMax,
      updatedRatingName,
      updatedAiPrompt,
      updatedAiCount,
      updatedCards,
      updatedGroups
    } = editDialog

    const plainText = updatedText.replace(/<[^>]+>/g, '').trim()
    if (!plainText) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionTextEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    let trimmedChoices = updatedChoices.map((c) => c.trim()).filter((c) => c !== '')

    if (
      (updatedType === 'singleChoice' || updatedType === 'multipleChoice') &&
      trimmedChoices.length === 0
    ) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.choicesRequiredWarning'),
        severity: 'warning'
      })
      return
    }

    if (updatedType === 'ratingQuestion') {
      if (!updatedRatingName.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingNameWarning'),
          severity: 'warning'
        })
        return
      }
      if (updatedRatingMin >= updatedRatingMax) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingMinMaxWarning'),
          severity: 'warning'
        })
        return
      }
    }

    if (updatedType === 'aiModerator') {
      if (!updatedAiPrompt.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiPromptEmptyWarning'),
          severity: 'warning'
        })
        return
      }
      if (updatedAiCount < 1) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiCountMinWarning'),
          severity: 'warning'
        })
        return
      }
    }

    try {
      await updateDoc(doc(db, 'questions', question.id), {
        text: updatedText,
        questionType: updatedType,
        choices: trimmedChoices,
        allowOther: updatedAllowOther,
        requireMedia: updatedType === 'openEnded' ? updatedRequireMedia : false,
        isRequired: updatedRequired,
        ratingMin: updatedRatingMin,
        ratingMax: updatedRatingMax,
        ratingName: updatedRatingName,
        aiPrompt: updatedAiPrompt,
        aiCount: updatedAiCount,
        ...(updatedType === 'cardSorting'
          ? {
              cards: updatedCards,
              groups: updatedGroups
            }
          : {})
      })

      setEditDialog({
        open: false,
        question: null,
        updatedText: '',
        updatedType: 'openEnded',
        updatedChoices: [],
        updatedAllowOther: false,
        updatedRequireMedia: false,
        updatedRequired: false,
        updatedRatingMin: 1,
        updatedRatingMax: 5,
        updatedRatingName: '',
        updatedAiPrompt: '',
        updatedAiCount: 1
      })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.updateQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.updateQuestionError'),
        severity: 'error'
      })
    }
  }

  const handleDeleteQuestion = async (questionId) => {
    if (selectedQuestionForm?.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'),
        severity: 'warning'
      })
      return
    }
    if (!window.confirm(t('moderatorFormBuilder.deleteQuestionConfirm'))) return

    try {
      await deleteDoc(doc(db, 'questions', questionId))
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.deleteQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru silinirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.deleteQuestionError'),
        severity: 'error'
      })
    }
  }

  // ------------------ Soruların Sürükle-Bırak Sıralaması ------------------
  async function handleDragEnd(result) {
    if (!result.destination) return

    const reordered = Array.from(questions)
    const [movedItem] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, movedItem)
    setQuestions(reordered)

    try {
      for (let i = 0; i < reordered.length; i++) {
        const q = reordered[i]
        await updateDoc(doc(db, 'questions', q.id), {
          sortOrder: i
        })
      }
    } catch (err) {
      console.error('Sıra güncellenirken hata:', err)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionSortError'),
        severity: 'error'
      })
    }
  }

  // Yeni choice ekleme
  const handleAddChoice = () => {
    setNewChoices([...newChoices, ''])
  }
  const handleRemoveChoice = (index) => {
    const updated = [...newChoices]
    updated.splice(index, 1)
    setNewChoices(updated)
  }
  const handleChoiceChange = (index, value) => {
    const updated = [...newChoices]
    updated[index] = value
    setNewChoices(updated)
  }

  // Edit dialog choices
  const handleEditAddChoice = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedChoices: [...prev.updatedChoices, '']
    }))
  }
  const handleEditRemoveChoice = (index) => {
    setEditDialog((prev) => {
      const updated = [...prev.updatedChoices]
      updated.splice(index, 1)
      return { ...prev, updatedChoices: updated }
    })
  }
  const handleEditChoiceChange = (index, value) => {
    setEditDialog((prev) => {
      const updated = [...prev.updatedChoices]
      updated[index] = value
      return { ...prev, updatedChoices: updated }
    })
  }

  // Kütüphaneye form kaydetme
  const handleSaveToLibrary = async () => {
    if (!selectedQuestionForm || questions.length === 0) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.noFormOrQuestionsWarning'),
        severity: 'warning'
      })
      return
    }

    try {
      const libraryForm = {
        name: selectedQuestionForm.name,
        description: '', // İsteğe bağlı bir açıklama alanı ekleyebilirsiniz
        questions: questions.map(q => ({
          text: q.text,
          questionType: q.questionType,
          choices: q.choices || [],
          allowOther: q.allowOther || false,
          requireMedia: q.requireMedia || false,
          isRequired: q.isRequired || false,
          ratingMin: q.ratingMin || 1,
          ratingMax: q.ratingMax || 5,
          ratingName: q.ratingName || '',
          aiPrompt: q.aiPrompt || '',
          aiCount: q.aiCount || 1,
          cards: q.cards || [],
          groups: q.groups || []
        })),
        createdBy: currentUser.uid,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'formLibrary'), libraryForm);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.savedToLibrarySuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Kütüphaneye kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.savedToLibraryError'),
        severity: 'error'
      });
    }
  };

  // State ekleme
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [libraryForms, setLibraryForms] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Kütüphane formlarını çekme
  useEffect(() => {
    if (!libraryDialogOpen) return;

    setLoadingLibrary(true);
    const q = query(collection(db, 'formLibrary'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const forms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLibraryForms(forms);
        setLoadingLibrary(false);
      },
      (error) => {
        console.error('Kütüphane formları alınırken hata:', error);
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.libraryLoadError'),
          severity: 'error'
        });
        setLoadingLibrary(false);
      }
    );
    return () => unsubscribe();
  }, [libraryDialogOpen, t]);

  // Kütüphaneden formu projeye ekleme
  const handleImportFromLibrary = async (libraryForm) => {
    if (!selectedChannel) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.selectChannelFirst'),
        severity: 'warning'
      });
      return;
    }

    try {
      // Yeni bir questionForm oluştur
      const newFormRef = await addDoc(collection(db, 'questionForms'), {
        channelId: selectedChannel.id,
        name: `${libraryForm.name} (Kopya)`,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'draft'
      });

      // Soruları ekle
      const questionPromises = libraryForm.questions.map((q, index) =>
        addDoc(collection(db, 'questions'), {
          channelId: selectedChannel.id,
          questionFormId: newFormRef.id,
          text: q.text,
          questionType: q.questionType,
          choices: q.choices || [],
          allowOther: q.allowOther || false,
          requireMedia: q.requireMedia || false,
          isRequired: q.isRequired || false,
          ratingMin: q.ratingMin || 1,
          ratingMax: q.ratingMax || 5,
          ratingName: q.ratingName || '',
          aiPrompt: q.aiPrompt || '',
          aiCount: q.aiCount || 1,
          cards: q.cards || [],
          groups: q.groups || [],
          sortOrder: index,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp()
        })
      );

      await Promise.all(questionPromises);
      setLibraryDialogOpen(false);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.importedFromLibrarySuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Form import edilirken hata:', error);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.importedFromLibraryError'),
        severity: 'error'
      });
    }
  };

  // Kütüphaneden form silme
  const handleDeleteFromLibrary = async (formId) => {
    if (!window.confirm(t('moderatorFormBuilder.deleteLibraryFormConfirm'))) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'formLibrary', formId));
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.deleteLibraryFormSuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Kütüphaneden form silinirken hata:', error);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.deleteLibraryFormError'),
        severity: 'error'
      });
    }
  };

  const handleEditAddCard = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedCards: [...prev.updatedCards, { text: '', imageUrl: '' }]
    }))
  }
  
  // Edit Dialog için: Kart sil
  const handleEditRemoveCard = (index) => {
    setEditDialog((prev) => {
      const newCards = [...prev.updatedCards]
      newCards.splice(index, 1)
      return { ...prev, updatedCards: newCards }
    })
  }
  
  // Edit Dialog için: Kart metnini güncelle
  const handleEditCardTextChange = (index, text) => {
    setEditDialog((prev) => {
      const newCards = [...prev.updatedCards]
      newCards[index].text = text
      return { ...prev, updatedCards: newCards }
    })
  }
  
  // Edit Dialog için: Kart fotoğrafı yükle
  const handleEditUploadCardImage = async (index) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        setEditDialog((prev) => {
          const newCards = [...prev.updatedCards]
          newCards[index].imageUrl = url
          return { ...prev, updatedCards: newCards }
        })
      } catch (err) {
        console.error('Kart fotoğrafı yüklenirken hata:', err)
        setSnackbar({
          open: true,
          message: 'Fotoğraf yüklenirken hata oluştu.',
          severity: 'error'
        })
      }
    }
    input.click()
  }
  
  // Edit Dialog için: Grup ekle
  const handleEditAddGroup = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedGroups: [...prev.updatedGroups, '']
    }))
  }
  
  // Edit Dialog için: Grup sil
  const handleEditRemoveGroup = (index) => {
    setEditDialog((prev) => {
      const newGroups = [...prev.updatedGroups]
      newGroups.splice(index, 1)
      return { ...prev, updatedGroups: newGroups }
    })
  }
  
  // Edit Dialog için: Grup ismini güncelle
  const handleEditGroupChange = (index, value) => {
    setEditDialog((prev) => {
      const newGroups = [...prev.updatedGroups]
      newGroups[index] = value
      return { ...prev, updatedGroups: newGroups }
    })
  }



  // ------------------------------------------------------
  //                   Render
  // ------------------------------------------------------
  return (
    <Container sx={{ mt: 4, backgroundColor: '#f9f9f9', padding: 4, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <CodeIcon sx={{ mr: 1 }} />
        {t('moderatorFormBuilder.pageTitle')}
      </Typography>

      {/* 1) Kanal Seçimi */}
      {!selectedChannel ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <ForumIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="div">
              {t('moderatorFormBuilder.assignedChannels')}
            </Typography>
          </Box>
          <Divider />

          {loadingChannels ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {channels.length > 0 ? (
                  channels.map((channel) => {
                    // YENİ: Kanalın workspace'ini bulalım
                    const workspaceName = channel.workspaceId
                      ? workspaceMap[channel.workspaceId] || 'Unknown Workspace'
                      : 'No Workspace'
  
                    return (
                      <ListItem
                        button
                        key={channel.id}
                        onClick={() => setSelectedChannel(channel)}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: '#f5f5f5',
                          '&:hover': { bgcolor: '#e9e9e9' }
                        }}
                      >
                        <ListItemText
                          // Burada workspace adını yukarı, kanal adını alt başlık olarak gösterebilirsiniz
                          primary={
                            <>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontStyle: 'italic' }}>
                                {workspaceName}
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {channel.name}
                              </Typography>
                            </>
                          }
                          secondary={`${t('moderatorFormBuilder.memberCount')}: ${
                            channel.memberDetails ? channel.memberDetails.length : 0
                          }`}
                        />
                      </ListItem>
                    )
                  })
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  {t('moderatorFormBuilder.noAssignedChannel')}
                </Typography>
              )}
            </List>
          )}
        </Paper>
      ) : (
        <>
          {/* Geri Dön */}
          <Box display="flex" alignItems="center" mb={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setSelectedChannel(null)
                setSelectedQuestionForm(null)
              }}
              sx={{ mr: 2 }}
            >
              {t('moderatorFormBuilder.backButton')}
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
              {`${selectedChannel.name} ${t('moderatorFormBuilder.channelTitleSuffix')}`}
            </Typography>
          </Box>

          {/* 2) Soru Formlarını Listele */}
          {!selectedQuestionForm ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('moderatorFormBuilder.formListTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loadingQuestionForms ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {questionForms.length > 0 ? (
                    <List sx={{ mb: 2 }}>
                      {questionForms.map((form) => (
                        <ListItem
                          button
                          key={form.id}
                          onClick={() => setSelectedQuestionForm(form)}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f2f2f2' }
                          }}
                        >
                          <ListItemText
                            primary={form.name}
                            secondary={
                              <>
                                <Typography variant="body2">
                                  {t('moderatorFormBuilder.status')}:{' '}
                                  {form.status || t('moderatorFormBuilder.draft')}
                                </Typography>
                                <Typography variant="caption">
                                  {t('moderatorFormBuilder.createdDate')}:{' '}
                                  {form.createdAt?.toDate().toLocaleString()}
                                </Typography>
                              </>
                            }
                          />
                          {/* Durum Butonları */}
                          <Box sx={{ mr: 2 }}>
                            {form.status === 'draft' && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePublish(form)
                                }}
                              >
                                {/** "Yayınla" sabit Türkçe ise translation'a ekleyin */}
                                {t('moderatorFormBuilder.formPublishButton')}
                              </Button>
                            )}
                            {form.status === 'published' && (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ mr: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleMarkComplete(form)
                                  }}
                                >
                                  {/** "Tamamla" sabit Türkçe ise translation'a ekleyin */}
                                  {t('moderatorFormBuilder.formCompletedButton')}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  sx={{ mr: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRevertToDraft(form)
                                  }}
                                >
                                  {/** "Taslağa Dön" da ekleyebilirsiniz */}
                                  {t('moderatorFormBuilder.formDraftButton')}
                                  
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleSendReminderEmails(form)
                                  }}
                                >
                                  {t('moderatorFormBuilder.sendReminderButton')}
                                </Button>
                              </>
                            )}
                            {form.status === 'completed' && (
                              <Typography variant="body2" color="textSecondary">
                                {t('moderatorFormBuilder.formCompletedLabel')}
                              </Typography>
                            )}
                          </Box>
                          {/* Yeni RENAME IconButton */}
                          <IconButton
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenRenameDialog(form)
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteQuestionForm(form.id)
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {t('moderatorFormBuilder.noFormsYet')}
                    </Typography>
                  )}
                </>
              )}

              {/* Yeni Form Ekleme */}
              <Box display="flex" mt={2}>
                <TextField
                  label={t('moderatorFormBuilder.newFormName')}
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateQuestionForm}
                  sx={{ ml: 2 }}
                >
                  {t('moderatorFormBuilder.add')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setLibraryDialogOpen(true)}
                  sx={{ ml: 2 }}
                >
                  {t('moderatorFormBuilder.openLibrary')}
                </Button>
              </Box>
            </Paper>
          ) : (
            <>
              {/* 3) Mevcut Form İçindeki Sorular */}
              <Box display="flex" alignItems="center" mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedQuestionForm(null)}
                  sx={{ mr: 2 }}
                >
                  {t('moderatorFormBuilder.goBackForms')}
                </Button>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {selectedQuestionForm.name} {t('moderatorFormBuilder.formListTitle')}{' '}
                  ({selectedQuestionForm.status || t('moderatorFormBuilder.draft')})
                </Typography>
              </Box>

              {/* Yeni Soru Oluştur (Sadece draft durumunda) */}
              {selectedQuestionForm.status === 'draft' && (
                <Paper variant="outlined" sx={{ mb: 4, p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'medium' }}>
                    {t('moderatorFormBuilder.newQuestionTitle')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" gutterBottom>
                    {t('moderatorFormBuilder.inlineQuillHint')}
                  </Typography>

                  <InlineQuillEditor
                    value={newQuestionText}
                    onChange={(val) => setNewQuestionText(val)}
                  />

                  <Box mt={2}>
                    <FormControl fullWidth>
                      <InputLabel id="question-type-label">
                        {t('moderatorFormBuilder.questionTypeLabel')}
                      </InputLabel>
                      <Select
                        labelId="question-type-label"
                        value={newQuestionType}
                        label={t('moderatorFormBuilder.questionTypeLabel')}
                        onChange={(e) => setNewQuestionType(e.target.value)}
                      >
                        <MenuItem value="openEnded">{t('moderatorFormBuilder.openEnded')}</MenuItem>
                        <MenuItem value="singleChoice">
                          {t('moderatorFormBuilder.singleChoice')}
                        </MenuItem>
                        <MenuItem value="multipleChoice">
                          {t('moderatorFormBuilder.multipleChoice')}
                        </MenuItem>
                        <MenuItem value="ratingQuestion">
                          {t('moderatorFormBuilder.ratingQuestion')}
                        </MenuItem>
                        <MenuItem value="videoQuestion">
                          {t('moderatorFormBuilder.videoQuestion')}
                        </MenuItem>
                        <MenuItem value="photoCollage">
                          {t('moderatorFormBuilder.photoCollage')}
                        </MenuItem>
                        <MenuItem value="aiModerator">
                          {t('moderatorFormBuilder.aiModerator')}
                        </MenuItem>
                        <MenuItem value="infoQuestion">{t('moderatorFormBuilder.infoQuestion')} </MenuItem>
                        <MenuItem value="cardSorting">{t('moderatorFormBuilder.cardSorting')}</MenuItem>

                      </Select>
                    </FormControl>
                  </Box>

                  {newQuestionType === 'openEnded' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newRequireMedia}
                          onChange={(e) => setNewRequireMedia(e.target.checked)}
                        />
                      }
                      label={t('moderatorFormBuilder.requireMediaLabel')}
                    />
                  )}

                  {(newQuestionType === 'singleChoice' || newQuestionType === 'multipleChoice') && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {t('moderatorFormBuilder.optionsLabel')}
                      </Typography>
                      {newChoices.map((choice, index) => (
                        <Box key={index} display="flex" alignItems="center" mt={1}>
                          {newQuestionType === 'singleChoice' ? (
                            <Radio disabled />
                          ) : (
                            <Checkbox disabled />
                          )}
                          <TextField
                            value={choice}
                            onChange={(e) => handleChoiceChange(index, e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={`Option ${index + 1}`}
                            sx={{ mr: 1 }}
                          />
                          <IconButton
                            onClick={() => handleRemoveChoice(index)}
                            disabled={newChoices.length === 1 && index === 0}
                            color="error"
                          >
                            <ClearIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddChoice}
                        sx={{ mt: 1 }}
                      >
                        {t('moderatorFormBuilder.addChoice')}
                      </Button>
                      <FormControlLabel
                        sx={{ display: 'block', mt: 2 }}
                        control={
                          <Checkbox
                            checked={newAllowOther}
                            onChange={(e) => setNewAllowOther(e.target.checked)}
                          />
                        }
                        label={t('moderatorFormBuilder.allowOther')}
                      />
                    </Box>
                  )}

                  {newQuestionType === 'ratingQuestion' && (
                    <Box mt={2}>
                      <TextField
                        label={t('moderatorFormBuilder.ratingScaleName')}
                        fullWidth
                        value={newRatingName}
                        onChange={(e) => setNewRatingName(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Box display="flex" gap={2}>
                        <TextField
                          type="number"
                          label={t('moderatorFormBuilder.ratingMin')}
                          value={newRatingMin}
                          onChange={(e) => setNewRatingMin(Number(e.target.value))}
                        />
                        <TextField
                          type="number"
                          label={t('moderatorFormBuilder.ratingMax')}
                          value={newRatingMax}
                          onChange={(e) => setNewRatingMax(Number(e.target.value))}
                        />
                      </Box>
                    </Box>
                  )}

                  {newQuestionType === 'aiModerator' && (
                    <Box mt={2}>
                      <TextField
                        label={t('moderatorFormBuilder.aiPromptLabel')}
                        value={newAiPrompt}
                        onChange={(e) => setNewAiPrompt(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        type="number"
                        label={t('moderatorFormBuilder.aiCountLabel')}
                        value={newAiCount}
                        onChange={(e) => setNewAiCount(Number(e.target.value))}
                        inputProps={{ min: 1, max: 5 }}
                        fullWidth
                      />
                    </Box>
                  )}

                  {newQuestionType === 'cardSorting' && (
                    <>
                      {/* Kartlar Bölümü */}
                      <Box mt={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          Kartlar
                        </Typography>
                        {newCards.map((card, index) => (
                          <Box key={index} display="flex" alignItems="center" mt={1}>
                            <TextField
                              value={card.text}
                              onChange={(e) => handleCardTextChange(index, e.target.value)}
                              placeholder={`Kart ${index + 1}`}
                              sx={{ mr: 1 }}
                            />
                            <Button onClick={() => handleUploadCardImage(index)}>
                              {card.imageUrl ? 'Fotoğrafı Değiştir' : 'Fotoğraf Yükle'}
                            </Button>
                            {card.imageUrl && (
                              <img src={card.imageUrl} alt="Önizleme" style={{ width: 50, height: 50, marginRight: 8 }} />
                            )}
                            <IconButton onClick={() => handleRemoveCard(index)}>
                              <ClearIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button variant="outlined" onClick={handleAddCard} sx={{ mt: 1 }}>
                          Kart Ekle
                        </Button>
                      </Box>

                      {/* Gruplar Bölümü */}
                      <Box mt={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          Gruplar
                        </Typography>
                        {newGroups.map((group, index) => (
                          <Box key={index} display="flex" alignItems="center" mt={1}>
                            <TextField
                              value={group}
                              onChange={(e) => handleGroupChange(index, e.target.value)}
                              placeholder={`Grup ${index + 1}`}
                              sx={{ mr: 1 }}
                            />
                            <IconButton onClick={() => handleRemoveGroup(index)}>
                              <ClearIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button variant="outlined" onClick={handleAddGroup} sx={{ mt: 1 }}>
                          Grup Ekle
                        </Button>
                      </Box>
                    </>
                  )}

                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newRequired}
                          onChange={(e) => setNewRequired(e.target.checked)}
                        />
                      }
                      label={t('moderatorFormBuilder.questionRequiredLabel')}
                    />
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddQuestion}
                      fullWidth
                    >
                      {t('moderatorFormBuilder.add')}
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Sorular Listesi (Drag&Drop) */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <ForumIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {t('moderatorFormBuilder.existingQuestions')}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSaveToLibrary}
                >
                  {t('moderatorFormBuilder.saveToLibrary')}
                </Button>
                <Divider sx={{ mb: 2 }} />
                {loadingQuestions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="questionsDroppable">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {questions.length > 0 ? (
                            questions.map((question, index) => (
                              <Draggable
                                key={question.id}
                                draggableId={question.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    sx={{
                                      mb: 2,
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor: '#fafafa',
                                      transition: 'box-shadow 0.2s ease',
                                      boxShadow: snapshot.isDragging
                                        ? '0 0 10px rgba(0,0,0,0.2)'
                                        : 'none',
                                      ...provided.draggableProps.style
                                    }}
                                  >
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={8}>
                                      <Typography
                                          variant="body1"
                                          dangerouslySetInnerHTML={{ __html: question.text }}
                                          sx={{
                                            mb: 1,
                                            '& .ql-align-center': { textAlign: 'center' },
                                            '& .ql-align-right': { textAlign: 'right' },
                                            '& .ql-align-justify': { textAlign: 'justify' },
                                            '& img': {
                                              maxWidth: '100%',
                                              height: 'auto',
                                              display: 'block' // Opsiyonel: Fotoğrafların düzgün hizalanmasını sağlar
                                            }
                                          }}
                                        />
                                        <Typography variant="caption" color="textSecondary">
                                          {t('moderatorFormBuilder.questionTypePrefix')}:{' '}
                                          {question.questionType}
                                        </Typography>

                                        {/* Tek/Çok seçimli seçenekler veya Rating vb. */}
                                        {/* ... */}
                                        {question.isRequired && (
                                          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                            {t('moderatorFormBuilder.requiredQuestionMark')}
                                          </Typography>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="flex-end">
                                          <IconButton
                                            color="primary"
                                            disabled={selectedQuestionForm?.status !== 'draft'}
                                            onClick={() => handleEditQuestion(question)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <IconButton
                                            color="error"
                                            disabled={selectedQuestionForm?.status !== 'draft'}
                                            onClick={() => handleDeleteQuestion(question.id)}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {t('moderatorFormBuilder.noQuestionsYet')}
                            </Typography>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </Paper>
            </>
          )}
        </>
      )}

      {/* Düzenleme Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() =>
          setEditDialog({
            open: false,
            question: null,
            updatedText: '',
            updatedType: 'openEnded',
            updatedChoices: [],
            updatedAllowOther: false,
            updatedRequireMedia: false,
            updatedRequired: false,
            updatedRatingMin: 1,
            updatedRatingMax: 5,
            updatedRatingName: '',
            updatedAiPrompt: '',
            updatedAiCount: 1
          })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('moderatorFormBuilder.editQuestionDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('moderatorFormBuilder.editQuillHint')}
          </Typography>
          <InlineQuillEditor
            value={editDialog.updatedText}
            onChange={(val) =>
              setEditDialog((prev) => ({ ...prev, updatedText: val }))
            }
          />
          <Box mt={2}>
            <FormControl fullWidth>
              <InputLabel id="edit-question-type-label">
                {t('moderatorFormBuilder.questionTypeLabel')}
              </InputLabel>
              <Select
                labelId="edit-question-type-label"
                value={editDialog.updatedType}
                label={t('moderatorFormBuilder.questionTypeLabel')}
                onChange={(e) =>
                  setEditDialog((prev) => ({ ...prev, updatedType: e.target.value }))
                }
              >
                <MenuItem value="openEnded">{t('moderatorFormBuilder.openEnded')}</MenuItem>
                <MenuItem value="singleChoice">{t('moderatorFormBuilder.singleChoice')}</MenuItem>
                <MenuItem value="multipleChoice">{t('moderatorFormBuilder.multipleChoice')}</MenuItem>
                <MenuItem value="ratingQuestion">{t('moderatorFormBuilder.ratingQuestion')}</MenuItem>
                <MenuItem value="videoQuestion">{t('moderatorFormBuilder.videoQuestion')}</MenuItem>
                <MenuItem value="photoCollage">{t('moderatorFormBuilder.photoCollage')}</MenuItem>
                <MenuItem value="aiModerator">{t('moderatorFormBuilder.aiModerator')}</MenuItem>
                <MenuItem value="infoQuestion">{t('moderatorFormBuilder.infoQuestion')}</MenuItem>
                <MenuItem value="infoQuestion">{t('moderatorFormBuilder.infoQuestion')}</MenuItem>
                <MenuItem value="cardSorting">{t('moderatorFormBuilder.cardSorting')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {editDialog.updatedType === 'openEnded' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={editDialog.updatedRequireMedia}
                  onChange={(e) =>
                    setEditDialog((prev) => ({ ...prev, updatedRequireMedia: e.target.checked }))
                  }
                />
              }
              label={t('moderatorFormBuilder.requireMediaLabel')}
            />
          )}

          {/* Tek/Çok seçimli seçenekler */}
          {(editDialog.updatedType === 'singleChoice' ||
            editDialog.updatedType === 'multipleChoice') && (
            <Box mt={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {t('moderatorFormBuilder.optionsLabel')}
              </Typography>
              {editDialog.updatedChoices.map((choice, index) => (
                <Box key={index} display="flex" alignItems="center" mt={1}>
                  {editDialog.updatedType === 'singleChoice' ? (
                    <Radio disabled size="small" />
                  ) : (
                    <Checkbox disabled size="small" />
                  )}
                  <TextField
                    value={choice}
                    onChange={(e) => handleEditChoiceChange(index, e.target.value)}
                    fullWidth
                    size="small"
                    placeholder={`Option ${index + 1}`}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    onClick={() => handleEditRemoveChoice(index)}
                    disabled={editDialog.updatedChoices.length === 1 && index === 0}
                    color="error"
                  >
                    <ClearIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleEditAddChoice}
                sx={{ mt: 1 }}
              >
                {t('moderatorFormBuilder.addChoice')}
              </Button>
              <FormControlLabel
                sx={{ display: 'block', mt: 2 }}
                control={
                  <Checkbox
                    checked={editDialog.updatedAllowOther}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, updatedAllowOther: e.target.checked }))
                    }
                  />
                }
                label={t('moderatorFormBuilder.allowOther')}
              />
            </Box>
          )}

          {/* Rating Question */}
          {editDialog.updatedType === 'ratingQuestion' && (
            <Box mt={2}>
              <TextField
                label={t('moderatorFormBuilder.ratingScaleName')}
                fullWidth
                value={editDialog.updatedRatingName}
                onChange={(e) =>
                  setEditDialog((prev) => ({ ...prev, updatedRatingName: e.target.value }))
                }
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={2}>
                <TextField
                  type="number"
                  label={t('moderatorFormBuilder.ratingMin')}
                  value={editDialog.updatedRatingMin}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      updatedRatingMin: Number(e.target.value)
                    }))
                  }
                />
                <TextField
                  type="number"
                  label={t('moderatorFormBuilder.ratingMax')}
                  value={editDialog.updatedRatingMax}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      updatedRatingMax: Number(e.target.value)
                    }))
                  }
                />
              </Box>
            </Box>
          )}

          {/* AI-Moderator */}
          {editDialog.updatedType === 'aiModerator' && (
            <Box mt={2}>
              <TextField
                label={t('moderatorFormBuilder.aiPromptLabel')}
                fullWidth
                value={editDialog.updatedAiPrompt}
                onChange={(e) =>
                  setEditDialog((prev) => ({
                    ...prev,
                    updatedAiPrompt: e.target.value
                  }))
                }
                sx={{ mb: 2 }}
              />
              <TextField
                type="number"
                label={t('moderatorFormBuilder.aiCountLabel')}
                fullWidth
                value={editDialog.updatedAiCount}
                onChange={(e) =>
                  setEditDialog((prev) => ({
                    ...prev,
                    updatedAiCount: Number(e.target.value)
                  }))
                }
                inputProps={{
                  min: 1,
                  max: 5
                }}
              />
            </Box>
          )}
          {editDialog.updatedType === 'cardSorting' && (
            <>
              {/* Kartlar Bölümü */}
              <Box mt={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Kartlar
                </Typography>
                {editDialog.updatedCards.map((card, index) => (
                  <Box key={index} display="flex" alignItems="center" mt={1}>
                    {/* Kart metni düzenleme */}
                    <TextField
                      value={card.text}
                      onChange={(e) => handleEditCardTextChange(index, e.target.value)}
                      placeholder={`Kart ${index + 1}`}
                      sx={{ mr: 1 }}
                    />

                    {/* Kart fotoğrafı yükleme */}
                    <Button onClick={() => handleEditUploadCardImage(index)}>
                      {card.imageUrl ? 'Fotoğrafı Değiştir' : 'Fotoğraf Yükle'}
                    </Button>
                    {card.imageUrl && (
                      <img
                        src={card.imageUrl}
                        alt="Önizleme"
                        style={{ width: 50, height: 50, marginRight: 8 }}
                      />
                    )}

                    {/* Kart silme */}
                    <IconButton onClick={() => handleEditRemoveCard(index)}>
                      <ClearIcon />
                    </IconButton>
                  </Box>
                ))}

                {/* Yeni Kart Ekle */}
                <Button variant="outlined" onClick={handleEditAddCard} sx={{ mt: 1 }}>
                  Kart Ekle
                </Button>
              </Box>

              {/* Gruplar Bölümü */}
              <Box mt={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Gruplar
                </Typography>
                {editDialog.updatedGroups.map((group, index) => (
                  <Box key={index} display="flex" alignItems="center" mt={1}>
                    <TextField
                      value={group}
                      onChange={(e) => handleEditGroupChange(index, e.target.value)}
                      placeholder={`Grup ${index + 1}`}
                      sx={{ mr: 1 }}
                    />
                    <IconButton onClick={() => handleEditRemoveGroup(index)}>
                      <ClearIcon />
                    </IconButton>
                  </Box>
                ))}

                {/* Yeni Grup Ekle */}
                <Button variant="outlined" onClick={handleEditAddGroup} sx={{ mt: 1 }}>
                  Grup Ekle
                </Button>
              </Box>
            </>
          )}

          {/* Zorunlu Alan */}
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editDialog.updatedRequired}
                  onChange={(e) =>
                    setEditDialog((prev) => ({ ...prev, updatedRequired: e.target.checked }))
                  }
                />
              }
              label={t('moderatorFormBuilder.questionRequiredLabel')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setEditDialog({
                open: false,
                question: null,
                updatedText: '',
                updatedType: 'openEnded',
                updatedChoices: [],
                updatedAllowOther: false,
                updatedRequireMedia: false,
                updatedRequired: false,
                updatedRatingMin: 1,
                updatedRatingMax: 5,
                updatedRatingName: '',
                updatedAiPrompt: '',
                updatedAiCount: 1
              })
            }
            color="secondary"
          >
            {t('moderatorFormBuilder.cancel')}
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            {t('moderatorFormBuilder.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameDialog.open}
        onClose={handleCancelRename}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Form Adını Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            label="Yeni Form Adı"
            value={renameDialog.newName}
            onChange={(e) =>
              setRenameDialog((prev) => ({ ...prev, newName: e.target.value }))
            }
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRename} color="secondary">
            İptal
          </Button>
          <Button onClick={handleSaveRename} color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={libraryDialogOpen}
        onClose={() => setLibraryDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('moderatorFormBuilder.formLibraryTitle')}</DialogTitle>
        <DialogContent>
          {loadingLibrary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : libraryForms.length > 0 ? (
            <List>
              {libraryForms.map((form) => (
                <ListItem
                  key={form.id}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleDeleteFromLibrary(form.id)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleImportFromLibrary(form)}
                      >
                        {t('moderatorFormBuilder.import')}
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={form.name}
                    secondary={`${form.questions.length} ${t('moderatorFormBuilder.questions')}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>{t('moderatorFormBuilder.noLibraryForms')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLibraryDialogOpen(false)}>
            {t('moderatorFormBuilder.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}